import type { Problem, WebSession } from './types';

/**
 * HTTP client of the web app.
 *
 * - The access token lives only in this object (memory). The refresh token is the httpOnly cookie `diwa_rt`
 *   that core sets on `/v1/auth/web/*` — JavaScript never sees it.
 * - Every call sends the bearer; a 401 triggers ONE shared refresh (single flight) and one retry.
 * - A failed refresh ends the session (`onSessionEnded`), the app then shows the login.
 * - Same-origin: in AWS the ALB routes `<zone>/v1/*` to core, locally Vite proxies it.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem | null,
  ) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
  get type(): string | undefined {
    return this.problem?.type;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the bearer and the refresh-retry (login, password reset). */
  anonymous?: boolean;
}

export type Fetch = (input: string, init: RequestInit) => Promise<Response>;

export class ApiClient {
  private token: string | null = null;
  private tokenExpiresAt = 0;
  private refreshing: Promise<WebSession | null> | null = null;
  onSessionEnded: (() => void) | null = null;
  onSession: ((s: WebSession) => void) | null = null;

  constructor(
    private readonly fetchImpl: Fetch = (input, init) => fetch(input, init),
    private readonly now: () => number = () => Date.now(),
  ) {}

  get hasToken(): boolean {
    return this.token !== null;
  }

  setSession(s: WebSession | null): void {
    this.token = s?.accessToken ?? null;
    this.tokenExpiresAt = s ? Date.parse(s.accessTokenExpiresAt) : 0;
    if (s) this.onSession?.(s);
  }

  /** A valid access token for other clients of core in this app (the embedded designer); refreshes if due. */
  async accessToken(): Promise<string | null> {
    if (!this.token || this.tokenExpiresAt - this.now() < 30_000) await this.refresh();
    return this.token;
  }

  /** Renews the access token with the refresh cookie. Concurrent callers share one request. */
  refresh(): Promise<WebSession | null> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        try {
          const r = await this.fetchImpl('/v1/auth/web/refresh', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json', accept: 'application/json' },
            body: '{}',
          });
          if (!r.ok) {
            this.setSession(null);
            return null;
          }
          const s = (await r.json()) as WebSession;
          this.setSession(s);
          return s;
        } catch {
          return null;
        } finally {
          this.refreshing = null;
        }
      })();
    }
    return this.refreshing;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    if (!opts.anonymous && (!this.token || this.tokenExpiresAt - this.now() < 30_000)) await this.refresh();
    let r = await this.send(method, path, opts);
    if (r.status === 401 && !opts.anonymous) {
      const s = await this.refresh();
      if (!s) {
        this.onSessionEnded?.();
        throw await toError(r);
      }
      r = await this.send(method, path, opts);
      if (r.status === 401) this.onSessionEnded?.();
    }
    if (!r.ok) throw await toError(r);
    if (r.status === 204 || r.status === 202) return undefined as T;
    const text = await r.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get<T>(path: string, query?: Query, signal?: AbortSignal) {
    return this.request<T>('GET', path, { ...(query ? { query } : {}), ...(signal ? { signal } : {}) });
  }
  post<T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'body'> = {}) {
    return this.request<T>('POST', path, { ...opts, body: body ?? {} });
  }
  put<T>(path: string, body: unknown, headers?: Record<string, string>) {
    return this.request<T>('PUT', path, { body, ...(headers ? { headers } : {}) });
  }
  patch<T>(path: string, body: unknown) {
    return this.request<T>('PATCH', path, { body });
  }
  delete<T>(path: string, opts: { query?: Query; body?: unknown } = {}) {
    return this.request<T>('DELETE', path, opts);
  }

  private send(method: string, path: string, opts: RequestOptions): Promise<Response> {
    const headers: Record<string, string> = { accept: 'application/json', ...opts.headers };
    if (this.token && !opts.anonymous) headers.authorization = `Bearer ${this.token}`;
    const init: RequestInit = { method, headers, credentials: 'same-origin' };
    if (opts.body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    if (opts.signal) init.signal = opts.signal;
    return this.fetchImpl(withQuery(path, opts.query), init);
  }
}

export function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

async function toError(r: Response): Promise<ApiError> {
  let problem: Problem | null = null;
  try {
    const text = await r.text();
    if (text) problem = JSON.parse(text) as Problem;
  } catch {
    problem = null;
  }
  return new ApiError(r.status, problem);
}

/** The one client of the app. */
export const api = new ApiClient();

/**
 * Uploads a file to the presigned URL from `POST /v1/media/uploads` (S3 in AWS). XHR instead of fetch for
 * the progress events. Content-Length is set by the browser from the file and matches the signed value.
 */
export function putFile(
  url: string,
  file: Blob,
  headers: Record<string, string>,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) {
      if (k.toLowerCase() === 'content-length') continue; // forbidden header, the browser sets it
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, null));
    xhr.onerror = () => reject(new ApiError(0, null));
    xhr.send(file);
  });
}
